import { Col, Row } from 'antd';
import React from 'react';

import { useSocketReport } from '@/hooks';
import { PreviousValues } from './PreviousValues';
import { ToolCardGroup } from '@/components/ToolCard';
import { useStepKeysContext } from './DefaultSelects/context';
import type { ErgonomicTools, REPORT_TYPE, ToolsReponse } from '@/hooks';
import { AdditionalComponents, DefaultSelects, SubStepKeys } from './index';

import * as S from '@/components/ui/CollapseCheck/styles';

enum Tools {
	niosh = 'niosh',
	kim_pp = 'kim_pp',
	kim_mho = 'kim_mho',
	kim_lhc = 'kim_lhc',
	strain_index = 'strain_index',
	liberty_mutual = 'liberty_mutual',
	back_compressive_force_estimation = 'back_compressive_force_estimation'
}

const reports: { [key in REPORT_TYPE]: Tools } = {
	niosh: Tools.niosh,
	'kim-pp': Tools.kim_pp,
	'strain-index': Tools.strain_index,
	'kim-mho': Tools.kim_mho,
	'kim-lhc': Tools.kim_lhc,
	'liberty-mutual': Tools.liberty_mutual,
	'back-compressive-force-estimation': Tools.back_compressive_force_estimation
};

interface StepKeyPros {
	subStepKeysComponent?: JSX.Element;
}

export function StepKey({ subStepKeysComponent }: Readonly<StepKeyPros>): JSX.Element {
	const { niosh, kimPushPull, kimMHO, strainIndex, libertyMutual, backCompressiveForceEstimation, kimLHC } =
		useSocketReport();
	const { file_id, stepKey, formFieldName, tools, tools_tooltip } = useStepKeysContext();

	const socketsValues: { [key: string]: boolean | undefined } = {
		niosh: niosh.status,
		'kim-mho': kimMHO.status,
		'kim-pp': kimPushPull.status,
		'kim-lhc': kimLHC.status,
		'strain-index': strainIndex.status,
		'liberty-mutual': libertyMutual.status,
		'back-compressive-force-estimation': backCompressiveForceEstimation.status
	};

	const ergonomicTools = stepKey.ergonomic_tool ?? [];
	const hasErgonomicTools = ergonomicTools.length > 0;

	function isActive(tool: ErgonomicTools): boolean {
		const reportTool = tools[reports[tool.description] as keyof ToolsReponse];
		return !!reportTool || !!socketsValues[tool.description];
	}

	if (stepKey?.sub_step_keys && stepKey.sub_step_keys.length > 0) {
		return subStepKeysComponent ?? <SubStepKeys subStepKeys={stepKey?.sub_step_keys} />;
	}

	return (
		<>
			<PreviousValues negative={true} />
			<S.BodyPadding>
				<Row justify="center">
					<Col sm={24} xxl={22}>
						<DefaultSelects stepKeyId={stepKey.id} formFieldName={formFieldName}>
							{hasErgonomicTools && (
								<ToolCardGroup
									fileId={file_id}
									tools={ergonomicTools}
									isActive={isActive}
									toolsTooltip={tools_tooltip}
								/>
							)}
							{stepKey.additional_items && (
								<AdditionalComponents additionalItems={stepKey.additional_items} />
							)}
						</DefaultSelects>
					</Col>
				</Row>
			</S.BodyPadding>
		</>
	);
}
